import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from 'react-google-autocomplete';
import { StanButton, StanInput } from '@/components';
import { useEmailOnboarding } from '@/features/email-flows/hooks';
import { type AddressFormData, addressSchema } from '@/features/email-flows/validations/address';
import { parseGooglePlaceAddress } from '@/features/email-flows/logic/emailFlows.logic';
import { BUTTON_LABELS, BUTTON_NAMES, ONBOARDING_CONTENT } from '@/features/email-flows/constants';
import { configSettings } from '@/lib/config';
import { LuMail } from 'react-icons/lu';
import { useState } from 'react';
import styles from '@/components/core-components/StanInput/StanInput.module.css';
import { cn } from '@/lib/utils/cn';

// Type for Google Places API
interface PlaceResult {
    address_components?: Array<{
        long_name: string;
        short_name: string;
        types: string[];
    }>;
}

interface EnterAddressProps {
    onComplete: () => void;
}

export const EnterAddress = ({ onComplete }: EnterAddressProps) => {
    const { submitAddress, isSubmitting } = useEmailOnboarding();
    const [isFocused, setIsFocused] = useState(false);

    // Validate Google Maps API key
    const apiKey = configSettings.googleMapsApiKey;
    if (!apiKey) {
        console.error('Google Maps API key is not configured');
    }

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<AddressFormData>({
        resolver: zodResolver(addressSchema),
        defaultValues: {
            street: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
        },
    });

    // Handle Google Places autocomplete selection
    const handlePlaceSelected = (place: PlaceResult) => {
        if (!place.address_components) {
            console.error('Google Places API returned incomplete data');
            return;
        }

        const parsedAddress = parseGooglePlaceAddress(place.address_components);

        // Update form values using setValue
        setValue('street', parsedAddress.street, { shouldValidate: true });
        setValue('city', parsedAddress.city, { shouldValidate: true });
        setValue('state', parsedAddress.state, { shouldValidate: true });
        setValue('postal_code', parsedAddress.postal_code, { shouldValidate: true });
        setValue('country', parsedAddress.country, { shouldValidate: true });
    };

    const onSubmit = async (data: AddressFormData) => {
        try {
            await submitAddress(data);
            onComplete();
        } catch (error) {
            // Error handling is done in the hook with stanNotify
            console.error('Failed to submit address:', error);
        }
    };

    return (
        <div className='mt-4 mb-12 flex w-full max-w-[400px] flex-col items-center justify-center gap-6'>
            {/* Icon */}
            <div className='p-4'>
                <LuMail className='text-semantic-fg-base size-[78px]' />
            </div>

            {/* Title */}
            <p className='text-semantic-fg-base w-full text-center text-[28px] leading-tight font-extrabold tracking-tight'>
                {ONBOARDING_CONTENT.ADDRESS_STEP.title}
            </p>

            {/* Subtitle */}
            <div className='text-gray font-inter w-full text-center text-base leading-snug'>
                <p className='mb-4'>{ONBOARDING_CONTENT.ADDRESS_STEP.subtitle}</p>
                <p>
                    {ONBOARDING_CONTENT.ADDRESS_STEP.learnMoreText}{' '}
                    <a
                        href={ONBOARDING_CONTENT.ADDRESS_STEP.learnMoreLink}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-brand-main hover:text-brand-dark underline'
                    >
                        {ONBOARDING_CONTENT.ADDRESS_STEP.learnMoreLinkText}
                    </a>
                    .
                </p>
            </div>

            {/* Form */}
            <form
                onSubmit={handleSubmit(onSubmit)}
                className='mt-4 flex w-full max-w-96 flex-col items-center justify-center gap-6'
            >
                <div className='flex w-full flex-col gap-3'>
                    <Controller
                        name='street'
                        control={control}
                        render={({ field }) => (
                            <div
                                className={`stan-input ${errors.street ? styles.negative : ''} ${isSubmitting ? styles.disabled : ''}`}
                            >
                                <div
                                    className={cn('p-0!', styles.wrapper, isFocused && styles.active)}
                                    onMouseEnter={e => {
                                        if (!isSubmitting) {
                                            e.currentTarget.style.borderColor = 'var(--bg-color-primary-brand-main)';
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (!isFocused && !errors.street) {
                                            e.currentTarget.style.borderColor = 'var(--border-color-disabled)';
                                        }
                                    }}
                                >
                                    <Autocomplete
                                        apiKey={configSettings.googleMapsApiKey}
                                        onPlaceSelected={handlePlaceSelected}
                                        options={{
                                            types: ['address'],
                                        }}
                                        className={cn('h-full px-4 py-3', styles.field)}
                                        placeholder='Street Address'
                                        value={field.value}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            field.onChange(e.target.value)
                                        }
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => setIsFocused(false)}
                                        disabled={isSubmitting}
                                        autoComplete='off'
                                        aria-label='Street Address'
                                        aria-describedby='street-address-hint'
                                    />
                                </div>
                                {errors.street && <span className={styles.hint}>{errors.street.message}</span>}
                            </div>
                        )}
                    />

                    <Controller
                        name='city'
                        control={control}
                        render={({ field }) => (
                            <StanInput
                                {...field}
                                placeholder='City'
                                error={!!errors.city}
                                hintText={errors.city?.message}
                                disabled={isSubmitting}
                                autoComplete='address-level2'
                                className='w-full'
                            />
                        )}
                    />

                    <div className='flex gap-3'>
                        <Controller
                            name='state'
                            control={control}
                            render={({ field }) => (
                                <StanInput
                                    {...field}
                                    placeholder='State/Province'
                                    error={!!errors.state}
                                    hintText={errors.state?.message}
                                    disabled={isSubmitting}
                                    autoComplete='address-level1'
                                    className='w-full min-w-0'
                                />
                            )}
                        />
                        <Controller
                            name='postal_code'
                            control={control}
                            render={({ field }) => (
                                <StanInput
                                    {...field}
                                    placeholder='Zip / Postal Code'
                                    error={!!errors.postal_code}
                                    hintText={errors.postal_code?.message}
                                    disabled={isSubmitting}
                                    autoComplete='postal-code'
                                    className='w-full min-w-0'
                                />
                            )}
                        />
                    </div>

                    <Controller
                        name='country'
                        control={control}
                        render={({ field }) => (
                            <StanInput
                                {...field}
                                placeholder='Country'
                                error={!!errors.country}
                                hintText={errors.country?.message}
                                disabled={isSubmitting}
                                autoComplete='country-name'
                                className='w-full'
                            />
                        )}
                    />
                </div>

                <div className='w-full'>
                    <StanButton
                        name={BUTTON_NAMES.ONBOARDING_CONTINUE}
                        type='submit'
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        className='w-full'
                    >
                        {BUTTON_LABELS.CONTINUE}
                    </StanButton>
                </div>
            </form>
        </div>
    );
};
